import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Input } from "./input";
import type { DropdownSelectProps } from "./select.types";

const Select = SelectPrimitive.Root;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex h-10 w-full items-center justify-between rounded-lg border border-black bg-white px-3 text-sm font-medium text-[#475467] shadow-[0_2px_0_0_#000000]",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0D41E1]",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 text-[#667085]" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Content
    ref={ref}
    position={position}
    className={cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-lg border border-black bg-white shadow-[0_8px_24px_rgba(0,0,0,0.16)]",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  >
    <SelectPrimitive.Viewport className="p-1">
      {children}
    </SelectPrimitive.Viewport>
  </SelectPrimitive.Content>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center rounded-md py-2 pl-8 pr-3 text-sm text-[#344054] outline-none",
      "focus:bg-[#F3F4F6] data-[state=checked]:font-semibold",
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4 text-[#0D41E1]" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectValue = SelectPrimitive.Value;

/** Compose the shared select primitives with optional searchable options. */
function DropdownSelect({
  id,
  value,
  defaultValue,
  onValueChange,
  options,
  placeholder,
  disabled,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
  testId,
  align = "start",
  className,
  contentClassName,
  portal = false,
  searchPlaceholder,
  emptyLabel,
  valueLabel,
}: DropdownSelectProps) {
  const [search, setSearch] = React.useState("");
  const visibleOptions = searchPlaceholder
    ? options.filter((option) =>
        (option.searchText ?? String(option.label))
          .toLocaleLowerCase()
          .includes(search.trim().toLocaleLowerCase()),
      )
    : options;
  const Portal = portal ? SelectPrimitive.Portal : React.Fragment;
  return (
    <Select
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      disabled={disabled}
      onOpenChange={() => setSearch("")}
    >
      <SelectTrigger
        id={id}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        data-testid={testId}
        className={cn(
          "shadow-none",
          "h-10 w-full border-black bg-white px-3 text-sm font-medium text-[#475467]",
          className,
        )}
      >
        <SelectValue placeholder={placeholder}>{valueLabel}</SelectValue>
      </SelectTrigger>
      <Portal>
        <SelectContent
          align={align}
          sideOffset={4}
          className={cn(
            "w-[var(--radix-select-trigger-width)] min-w-[var(--radix-select-trigger-width)] border-black shadow-none",
            contentClassName,
          )}
        >
          {searchPlaceholder && (
            <div className="sticky top-0 z-10 bg-white p-2">
              <Input
                aria-label={searchPlaceholder}
                placeholder={searchPlaceholder}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (
                    event.key !== "Escape" &&
                    event.key !== "Tab" &&
                    event.key !== "ArrowDown" &&
                    event.key !== "ArrowUp"
                  )
                    event.stopPropagation();
                }}
              />
            </div>
          )}
          {options.map((option) => (
            <div
              key={option.value}
              hidden={!visibleOptions.includes(option)}
              className={cn(
                "flex min-w-0 items-center",
                !visibleOptions.includes(option) && "hidden",
              )}
            >
              <SelectItem
                value={option.value}
                disabled={option.disabled}
                className="min-w-0 flex-1 rounded-none px-3 py-2.5 data-[state=checked]:bg-[#EBF5FF] [&>span:first-child]:hidden [&>span:last-child]:truncate"
              >
                {option.label}
              </SelectItem>
            </div>
          ))}
          {searchPlaceholder && visibleOptions.length === 0 && (
            <p role="status" className="p-3 text-sm text-muted-foreground">
              {emptyLabel}
            </p>
          )}
        </SelectContent>
      </Portal>
    </Select>
  );
}

export {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  DropdownSelect,
};
